import {computed, ref} from 'vue';
import {defineStore, storeToRefs} from 'pinia';
import {useLocalePrefix} from '#imports';
import {readonlyState} from '@malt/utils';
import type {FeaturesResponse, FetchError, TranslationPreferenceFeaturePreferenceEnum} from '@api';
import {useApis, useErrorHandler} from '~/composables';
import {useGenericErrorHandler} from '~/composables/useGenericErrorHandler';
import {useSearchUrlStore} from './search-url.store';

export const useSearchFeaturesStore = defineStore('search-features', () => {
    const {searchApi} = useApis();
    const {handleError} = useGenericErrorHandler();
    const {handleSearchProfilesApiError} = useErrorHandler();
    const {withLocalePrefix} = useLocalePrefix();

    const searchUrlStore = useSearchUrlStore();
    const {searchUrlParams} = storeToRefs(searchUrlStore);

    const features = ref<FeaturesResponse>();
    const isPotentiallyStrategyEligible = computed(() => features.value?.strategyEligibility.eligibility === 'POTENTIALLY');
    const isAbsolutelyStrategyEligible = computed(() => features.value?.strategyEligibility.eligibility === 'ABSOLUTELY');
    const isStrategyEligible = computed(() => isPotentiallyStrategyEligible.value ?? isAbsolutelyStrategyEligible.value);
    const profileViews = computed(() => features.value?.profileViews);
    const spellingCorrection = computed(() => {
        const suggestion = features.value?.spellingCorrection?.suggestion;
        if (suggestion) {
            return {
                suggestion,
                searchUrl: withLocalePrefix(`/s?${searchUrlStore.toUrlQueryParams({q: suggestion})}`),
            };
        }
        return null;
    });
    const translationPreference = ref<TranslationPreferenceFeaturePreferenceEnum>();

    async function fetchFeatures() {
        try {
            features.value = await searchApi.retrieveFeatures({
                q: searchUrlParams.value.query,
                p: searchUrlParams.value.page,
            });
            translationPreference.value = features.value.translationPreference.preference;
        } catch (error) {
            handleSearchProfilesApiError(error as FetchError);
        }
    }

    async function setTranslationPreference(preference: TranslationPreferenceFeaturePreferenceEnum) {
        try {
            await searchApi.updateProfileTranslationPreference({
                updateSearchTranslationPreferenceRequest: {
                    preference,
                },
            });
            translationPreference.value = preference;
        } catch (e) {
            handleError(e as FetchError);
        }
    }

    return {
        isAbsolutelyStrategyEligible: readonlyState(isAbsolutelyStrategyEligible),
        isPotentiallyStrategyEligible: readonlyState(isPotentiallyStrategyEligible),
        isStrategyEligible: readonlyState(isStrategyEligible),
        profileViews: readonlyState(profileViews),
        translationPreference: readonlyState(translationPreference),
        spellingCorrection: readonlyState(spellingCorrection),
        features: readonlyState(features),
        fetchFeatures,
        setTranslationPreference,
    };
});
